<template>
  <p v-if="roleListLoading === true">Loading ...</p>
  <div v-else>
    <v-text-field label="Filter..." v-model="search" outlined class="mt-5"
      ><template v-slot:prepend-inner>
        <v-icon class="mr-3">fas fa-search</v-icon>
      </template></v-text-field
    >
    <v-card outlined>
      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="roleList"
          :search="search"
          sort-by="name"
        >
          <template v-slot:item="{ item }">
            <tr @click="navigate(item.id)" class="och-tableRow">
              <td>
                <a>{{ item.name }}</a>
              </td>
              <td>
                {{ displayTemplateName(item.securityTemplateId) }}
              </td>
              <td>{{ typeof item.securityTemplateFinalizedBy === "string" ? item.securityTemplateFinalizedBy : "--" }}</td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

export default {
  computed: {
    serviceAreaId() {
      return this.organizationRecord && this.organizationRecord.serviceAreaId;
    },
    ...mapGetters({
      filterAnswerFunctionListByAttribute:
        "secAnswerSecFunction/filterByAttributeValue",
      filterOperatorList: "operator/filterByAttributeValue",
      filterOrganizationRoleAnswerListByAttribute:
        "secRoleOrganizationSecAnswer/filterByAttributeValue",
      filterPrimaryRoleFunctionListByAttribute:
        "secRolePrimarySecFunction/filterByAttributeValue",
      filterFunctionListByAttribute: "secFunction/filterByAttributeValue",
      filterFunctionInputListByAttribute:
        "secFunctionInput/filterByAttributeValue",
      filterAnswerByAttribute: "secAnswer/filterByAttributeValue",
      filterOrganizationRoleAnswerByAttribute:
        "secRoleOrganizationSecAnswer/filterByAttributeValue",
      getTemplateById: "template/getById",
    }),
    ...mapState({
      organizationId: state => state.session.organizationId,
      organizationRecord: state => state.organization.record,
      securityClassList: state => state.securityClass.list,
      serviceAreaId: state => state.session.user.serviceAreaId,
      templateList: state => state.template.list,
      userId: state => state.session.user.userId,
    }),
  },
  async created() {
    const serviceAreaId = this.serviceAreaId;
    const saGroupName = `ochin-crowd-sa${serviceAreaId}`

    const getSecurityRoles = async () => {
      let response;
      try {
        response = await fetch(`/api/member/${saGroupName}/security-role`)
      } catch (error) {
        return error;
      }

      let text;
      try {
        text = await response.text();
      } catch (error) {
        return error;
      }

      if (response.ok === false) return new Error(`API request failed: ${text}`)

      let data;
      try {
        data = JSON.parse(text)
      } catch (error) {
        return error;
      }

      return data
    }

    

    const [securityRoles] = await Promise.all([
      getSecurityRoles(),
      this.fetchAnswerFunctionList(),
      this.fetchFunctionInputList(),
      this.fetchOrganizationRoleAnswerList(),
      this.fetchPrimaryRoleFunctionList(),
      this.fetchSecurityClassList(),
      this.fetchTemplateList(),
      this.fetchUserList(),
      this.getOrganizationById(this.organizationId),
    ]);

    this.roleList = securityRoles
    this.roleListLoading = false;
  },
  data() {
    return {
      headers: [
        {
          align: "start",
          text: "Name",
          value: "name",
        },
        {
          sortable: false,
          text: "Security Template",
          value: "matchingTemplate",
        },
        { text: "Template Finalized By", value: "finalizedBy" },
      ],
      search: null,
      roleListLoading: true,
      roleList: [],
    };
  },
  methods: {
    async assignTemplate(item) {
      let securityTemplateId;
      if (item.securityTemplateId === undefined) {
        securityTemplateId = null;
      } else {
        securityTemplateId = item.securityTemplateId;
      }

      try {
        await this.updateOrganizationRole([
          item.id,
          {
            securityTemplateId,
            securityTemplateFinalizedBy: this.userId,
          },
        ]);
        await this.fetchRoleList();
      } catch (e) {
        console.error(e);
        this.notifyErrorRoleUpdate();
      }
    },
    customFilter(item, queryText) {
      const textId = item.id.toLowerCase();
      const textName = item.name.toLowerCase();
      const searchText = queryText.toLowerCase();

      return (
        textId.indexOf(searchText) > -1 || textName.indexOf(searchText) > -1
      );
    },
    filterTemplateList(role) {
      const functionInputList = this.getSecFunctionInputList(role);
      if (functionInputList.length) {
        const matchedClassList = this.securityClassList.filter(
          securityClass => {
            const { itemList } = securityClass;
            return !functionInputList.find(input => {
              // Checks if item matches and returns early if not.
              const matchedItem = itemList.find(
                item => item.item === input.point
              );
              if (!matchedItem) return true;

              const { callback } = this.filterOperatorList(
                "name",
                input.operator
              )[0];

              const { valueList } = matchedItem;
              if (!callback(valueList, input.value)) return true;
            });
          }
        );

        const matchedClassIdList = matchedClassList.map(item => item.id);
        return this.templateList.filter(item => {
          const isMatch =
            matchedClassIdList.includes(item.epiccareSecurityClass) ||
            matchedClassIdList.includes(item.cadenceSecurityClass) ||
            matchedClassIdList.includes(item.sharedSecurityClass) ||
            matchedClassIdList.includes(item.epiccareSecurityClass) ||
            matchedClassIdList.includes(item.cadenceSecurityClass) ||
            matchedClassIdList.includes(item.sharedSecurityClass) ||
            matchedClassIdList.includes(
              item.professionalBillingSecurityClass
            ) ||
            matchedClassIdList.includes(item.careEverywhereSecurityClass) ||
            matchedClassIdList.includes(item.radarSecurityClass) ||
            matchedClassIdList.includes(item.bridgesSecurityClass) ||
            matchedClassIdList.includes(item.reportingWorkbenchSecurityClass) ||
            matchedClassIdList.includes(item.roiSecurityClass) ||
            matchedClassIdList.includes(item.identitySecurityClass) ||
            matchedClassIdList.includes(item.inBasketSecurityClass) ||
            matchedClassIdList.includes(item.myChartSecurityClass) ||
            matchedClassIdList.includes(item.preludeSecurityClass) ||
            matchedClassIdList.includes(item.referralSecurityClass) ||
            matchedClassIdList.includes(item.webSuiteSecurityClass) ||
            matchedClassIdList.includes(item.willowSecurityClass) ||
            matchedClassIdList.includes(item.analyticsSecurityClass) ||
            matchedClassIdList.includes(
              item.infectionAndIsolationSecurityClass
            );

          if (!isMatch) return false;
          const { grouper } = item;
          if (grouper) {
            const [grouperType, grouperValue] = grouper.split(":");
            if (grouperType === "ACCESS") {
              return (
                grouperValue === "NONE" ||
                grouperValue.split(",").includes(`${this.serviceAreaId}`)
              );
            } else if (grouperType === "EXCEPT") {
              return !grouperValue.split(",").includes(`${this.serviceAreaId}`);
            }
          }
          return false;
        });
      }
    },
    getSecFunctionInputList(role) {
      // 1. Security Functions for Primary Role

      // Retrieve list of primary role and function associations for this organization role.
      const secFunctionPrimaryRoleList = this.filterPrimaryRoleFunctionListByAttribute(
        "secRolePrimaryId",
        role.secRolePrimaryId
      );

      // Extract a list of secFunctionId values from the list of primary roles and associated security functions.
      const secFunctionIdListForPrimaryRole = secFunctionPrimaryRoleList.map(
        item => {
          return item.secFunctionId;
        }
      );

      // 2. Security Functions for Organization Role

      // Retrieve list of organization role and answer associations.
      const organizationRoleAnswerList = this.filterOrganizationRoleAnswerListByAttribute(
        "secRoleOrganizationId",
        role.id
      );

      // Create list of secFunctionId values using secAnswerId values from organization role and answer associations.
      let secFunctionIdListForOrgRoleAnswer = [];
      if (organizationRoleAnswerList.length) {
        organizationRoleAnswerList.forEach(item => {
          const answerFunctionList = this.filterAnswerFunctionListByAttribute(
            "secAnswerId",
            item.secAnswerId
          );
          const secFunctionIdList = answerFunctionList.map(item => {
            return item.secFunctionId;
          });

          secFunctionIdListForOrgRoleAnswer = secFunctionIdListForOrgRoleAnswer.concat(
            secFunctionIdList
          );
        });
      }

      // 3. Generate a list of function inputs based on primary role base functions and the organization's answers.

      // Concat the two arrays of secFunctionId values (secFunctionIdListForPrimaryRole and secFunctionIdListForOrgRoleAnswer).
      const secFunctionIdListAll = secFunctionIdListForOrgRoleAnswer.concat(
        secFunctionIdListForPrimaryRole
      );

      // Deduplicate concatenated array.
      const dedupedFunctionIdList = [...new Set(secFunctionIdListAll)];

      // Create and return a function input list to be used for matching security templates.
      let functionInputList = [];
      dedupedFunctionIdList.forEach(item => {
        const functionInput = this.filterFunctionInputListByAttribute(
          "secFunctionId",
          item
        );
        functionInputList = functionInputList.concat(functionInput);
      });
      return functionInputList;
    },
    navigate(id) {
      this.$router.push({
        name: "organizationRoleEdit",
        params: { id },
      });
    },
    displayTemplateName(securityTemplateId) {
      const template = this.getTemplateById(securityTemplateId);
      if (template) {
        return `${template.id} - ${template.name}`;
      }
      return "--";
    },
    ...mapActions({
      fetchAnswerFunctionList: "secAnswerSecFunction/list",
      fetchFunctionInputList: "secFunctionInput/list",
      fetchOrganizationRoleAnswerList: "secRoleOrganizationSecAnswer/list",
      fetchPrimaryRoleFunctionList: "secRolePrimarySecFunction/list",
      fetchUserList: "user/list",
      updateOrganizationRole: "secRoleOrganization/update",
      fetchRoleList: "secRoleOrganization/list",
      fetchSecurityClassList: "securityClass/list",
      fetchTemplateList: "template/list",
      getOrganizationById: "organization/getByOrganizationId",
    }),
  },
  notifications: {
    notifyErrorRoleUpdate: {
      title: "Error Updating Organization Role",
      type: "error",
    },
  },
  props: ["organizationRoleId"],
};
</script>
